export interface ProcessStep {
  number: string;
  title: string;
  description: string;
  icon?: string;
}

export interface PageProcessProps {
  heading: string;
  subheading?: string;
  intro?: string;
  steps: ProcessStep[];
}

export default function PageProcess({
  heading,
  subheading,
  intro,
  steps,
}: PageProcessProps) {
  return (
    <section className="bg-ivory py-24 lg:py-32 overflow-hidden">
      <div className="max-w-screen-xl mx-auto px-8 lg:px-12">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-end mb-20">
          <div>
            {subheading && (
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-px bg-antique" />
                <span className="text-antique text-xs font-sans font-500 tracking-[0.24em] uppercase">{subheading}</span>
              </div>
            )}
            <h2
              className="font-serif text-inkblack"
              style={{ fontSize: 'clamp(2rem, 4vw, 3.2rem)', fontWeight: 700, lineHeight: 1.08, letterSpacing: '-0.015em' }}
            >
              {heading}
            </h2>
          </div>
          {intro && (
            <p className="text-pewter text-sm font-sans font-300 leading-relaxed lg:text-right">{intro}</p>
          )}
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0">
          {steps.map((step, i) => (
            <div
              key={i}
              className={`relative p-8 lg:p-10 ${
                i < steps.length - 1
                  ? 'border-b lg:border-b-0 lg:border-r border-mist'
                  : ''
              }`}
            >
              {/* Large background number */}
              <div
                className="font-serif text-antique/8 absolute top-4 right-4 leading-none select-none pointer-events-none"
                style={{ fontSize: '7rem', fontWeight: 700 }}
                aria-hidden="true"
              >
                {step.number}
              </div>

              {/* Step content */}
              <div className="relative">
                {/* Step number badge */}
                <div className="w-14 h-14 border border-antique/40 flex items-center justify-center mb-7 bg-ivory relative">
                  <span className="font-serif text-antique italic" style={{ fontSize: '1.8rem', fontWeight: 400 }}>
                    {step.number}
                  </span>
                </div>

                <h3 className="font-serif text-inkblack text-xl font-700 mb-3 leading-snug">{step.title}</h3>
                <p className="text-pewter text-sm font-sans font-300 leading-relaxed">{step.description}</p>

                {/* Arrow connector (desktop) */}
                {i < steps.length - 1 && (
                  <div className="hidden lg:flex absolute top-7 -right-6 w-5 h-5 items-center justify-center text-antique/40 z-10">
                    <i className="ri-arrow-right-s-line text-xl" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA strip */}
        <div className="mt-0 border-t border-mist pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-pewter text-xs font-sans font-300 leading-relaxed max-w-md">
            No appointment needed — walk in any day between 9am and 5pm. We&apos;re here to help you find exactly what you&apos;re looking for.
          </p>
          <a
            href="https://maps.google.com/?q=22899+S+Hwy+89+Yarnell+AZ+85362"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-antique text-inkblack text-xs font-sans font-600 tracking-[0.16em] uppercase px-8 py-3 whitespace-nowrap hover:bg-inkblack hover:text-antique transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-map-pin-line" />
            Visit the Store
          </a>
        </div>
      </div>
    </section>
  );
}
